import { Box, Button, Grid, Typography } from "@mui/material";
import { useState, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import useGenerateOptions, {
  useGenerateArrayFromObj,
} from "../../../../../components/input/hook/useGenerateOptions2";
import ProductWithPriceInvoice from "../../../../../components/product/ProductWithPrice";
import { useGetSuppliers } from "../../../../supplier/api/supplier/resolver";
import { useGetPaymentMethods } from "../../../api/invoice/paymentMethod/resolver";
import { useGetPurchaseInvoiceConstants } from "../../../api/invoice/purchase/constant/resolver";
import Input from "../../../../../components/input/Input-v2";
import { LoadingButton } from "../../../../../components/button/Button";
import { ModalFC } from "../../../../../components/modal/Modal";
import { useToast } from "../../../../../context/toast/ToastContext";
import { useAddPurchaseInvoice } from "../../../api/invoice/purchase/resolver";
import { MainContainerForm } from "../../../../../styles/Main.styles";
import { FromStringToDate } from "../../../../../utils/Helper";

export interface Form_Create_PurchaseInvoice {
  purchase_invoice_date: string;
  payment_method_id: string;
  purchase_invoice_status: string;
  payment_due_date: string | null;
  product_list: string;
  supplier_id: string;
}

export default function FormAdd() {
  const { show } = useToast();
  const refForm = useRef<any>();
  const [keyword, setKeyword] = useState<string>();
  const { control, handleSubmit, trigger, getValues, watch } =
    useForm<Form_Create_PurchaseInvoice>();
  const { mutate: mutateAdd, isLoading: loadingAdd } = useAddPurchaseInvoice();
  const { data: dataRawPaymentMethod, isLoading: fetchPaymentMethod } =
    useGetPaymentMethods({});
  const dataPaymentMethod = dataRawPaymentMethod?.data.data.data;
  const { data: dataPaymentMethodOptions } = useGenerateOptions({
    dataSource: dataPaymentMethod,
    keyForText: "payment_method_name",
    keyForValue: "id",
  });
  const { data: dataRawInvoiceConstant, isLoading: fetchInvoiceConstant } =
    useGetPurchaseInvoiceConstants();
  const dataInvoiceConstant = dataRawInvoiceConstant?.data.data.status;
  const { data: dataStatusInvoice } = useGenerateArrayFromObj({
    dataSource: dataInvoiceConstant,
  });
  const { data: dataRawSuppliers, isLoading: fetchSuppliers } = useGetSuppliers(
    { keyword }
  );
  const dataSuppliers = dataRawSuppliers?.data.data.data;
  const { data: dataSupplierOptions } = useGenerateOptions({
    dataSource: dataSuppliers,
    keyForText: "supplier_name",
    keyForValue: "id",
  });
  const [showModal, setShowModal] = useState(false);

  const handleFormSubmit: SubmitHandler<Form_Create_PurchaseInvoice> = (
    data
  ) => {
    const currentDate = new Date().toISOString();
    const body = {
      ...data,
      product_list: JSON.stringify(data.product_list),
      purchase_invoice_date: FromStringToDate(currentDate, true),
      payment_due_date: data.payment_due_date
        ? FromStringToDate(data.payment_due_date, true)
        : null,
    };
    mutateAdd({ ...body });
  };

  const handleOpenPaymentConfimation = () => {
    const dataProductList = getValues("product_list");
    if (dataProductList.length <= 0) {
      show("Tidak ada barang yang dipilih", "warning");
      return;
    }
    if (getValues("payment_method_id") === null) {
      show("Mohon memilih pembayaran", "warning");
      return;
    }
    refForm.current.requestSubmit();
  };

  return (
    <form ref={refForm} onSubmit={handleSubmit(handleFormSubmit)}>
      <MainContainerForm mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Input.Controller
              control={control}
              name="purchase_invoice_status"
              rules={{ required: "Kolom ini tidak boleh kosong" }}
              render={(field, fieldState) => (
                <Input.Select
                  isFetching={fetchInvoiceConstant}
                  label="Status"
                  options={dataStatusInvoice}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input.Controller
              control={control}
              name="supplier_id"
              rules={{ required: "Kolom ini tidak boleh kosong" }}
              render={(field, fieldState) => (
                <Input.Autocomplete
                  isFetching={fetchSuppliers}
                  label="Supplier"
                  options={dataSupplierOptions}
                  value={field.value}
                  onClick={(e) => field.onChange(e.value)}
                  onChange={(e) => setKeyword(e)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          {watch("purchase_invoice_status") ===
            dataInvoiceConstant?.["Belum Lunas"] && (
            <Grid item xs={12} md={6}>
              <Input.Controller
                control={control}
                name="payment_due_date"
                render={(field) => (
                  <Input.Date
                    isFetching={fetchSuppliers}
                    label="Tanggal Jatuh Tempo"
                    value={field.value}
                    onChange={(e) => field.onChange(e)}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </MainContainerForm>
      <MainContainerForm>
        <Grid item xs={12} md={12}>
          <Input.Controller
            control={control}
            name="product_list"
            render={(field) => (
              <ProductWithPriceInvoice onChange={(e) => field.onChange(e)} />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display={"flex"} justifyContent={"flex-end"} mt={2} gap={2}>
            <LoadingButton
              isLoading={false}
              text={"Buat"}
              onClick={async () => {
                const isValid = await trigger([
                  "product_list",
                  "purchase_invoice_status",
                ]);
                if (isValid) {
                  setShowModal(true);
                }
              }}
            />
          </Box>
        </Grid>
      </MainContainerForm>
      <ModalFC open={showModal} onClose={() => setShowModal(false)}>
        <Box minHeight={150}>
          <Typography variant="h6" mb={1}>
            Apakah anda yakin ?
          </Typography>
          {watch("purchase_invoice_status") === dataInvoiceConstant?.Lunas && (
            <Input.Controller
              control={control}
              name="payment_method_id"
              render={(field, fieldState) => (
                <Input.Select
                  isFetching={fetchPaymentMethod}
                  label="Metode Pembayaran"
                  options={dataPaymentMethodOptions}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
          <Box display={"flex"} justifyContent={"flex-end"} mt={2} gap={2}>
            <Button variant="outlined" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <LoadingButton
              onClick={() => handleOpenPaymentConfimation()}
              isLoading={loadingAdd}
              text={"Konfirmasi"}
            />
          </Box>
        </Box>
      </ModalFC>
    </form>
  );
}
